
import { Badge } from "@/components/ui/badge";
import { CouponInput } from "./CouponInput";
import { useState } from "react";

interface CartItem {
  product: {
    name: string;
    price: number;
    brand?: string;
    category?: string;
  };
  quantity: number;
}

interface OrderSummaryProps {
  cartItems: CartItem[];
  subtotal: number;
  shippingCost: number;
  total: number;
  onCouponApply?: (code: string, discountPercentage: number) => void;
}

export const OrderSummary = ({ 
  cartItems, 
  subtotal, 
  shippingCost, 
  total: initialTotal,
  onCouponApply 
}: OrderSummaryProps) => {
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [appliedCouponCode, setAppliedCouponCode] = useState<string>("");
  
  const discountAmount = (subtotal * discountPercentage) / 100;
  const total = initialTotal - discountAmount;

  // Check for Test checkout product
  const hasTestCheckout = cartItems?.some(item => 
    item.product?.name?.toLowerCase().includes('test checkout')
  ) || false;

  // Calculate Wag'n Tails toys count
  const wagnTailsToyCount = cartItems?.reduce((count, item) => {
    if (
      item.product?.brand?.toLowerCase() === "wag'n tails" && 
      item.product?.category?.toLowerCase() === 'toys'
    ) {
      return count + item.quantity;
    }
    return count;
  }, 0) || 0;

  // Determine if shipping should be free (priority order)
  const isShippingFree = hasTestCheckout || wagnTailsToyCount >= 2 || subtotal >= 30;

  const handleCouponApply = (code: string, discount: number) => {
    setDiscountPercentage(discount);
    setAppliedCouponCode(code);
    if (onCouponApply) {
      onCouponApply(code, discount);
    }
  };

  return (
    <div className="mb-8 p-4 bg-gray-50 rounded-lg">
      <h2 className="text-lg font-semibold mb-4">Order Summary</h2>
      <div className="space-y-2">
        {cartItems?.map((item, index) => (
          <div key={`${item.product?.name}-${index}`} className="flex justify-between">
            <span>{item.product?.name} (x{item.quantity})</span>
            <span>£{((item.product?.price || 0) * item.quantity).toFixed(2)}</span>
          </div>
        ))}
        <div className="pt-2 border-t">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>£{subtotal.toFixed(2)}</span>
          </div>
          {discountPercentage > 0 && (
            <div className="flex justify-between text-green-600">
              <span>Discount ({discountPercentage}%)</span>
              <span>-£{discountAmount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between items-center">
            <span>Shipping</span>
            <div className="text-right">
              {isShippingFree ? (
                <Badge variant="secondary" className="ml-2">FREE</Badge>
              ) : (
                <span>£{shippingCost.toFixed(2)}</span>
              )}
            </div>
          </div>
          {isShippingFree ? (
            <div className="text-sm text-green-600 mt-1">
              {hasTestCheckout 
                ? "Free shipping applied! Test checkout detected."
                : wagnTailsToyCount >= 2 
                ? "Free shipping applied! 2+ Wag'n Tails toys in cart."
                : "Free shipping applied! Order over £30."
              }
            </div>
          ) : (
            <div className="text-sm text-blue-600 mt-1">
              Add £{(30 - subtotal).toFixed(2)} more to qualify for free shipping!
            </div>
          )}
          <CouponInput onApply={handleCouponApply} />
          <div className="flex justify-between font-bold mt-2 pt-2 border-t">
            <span>Total</span>
            <span>£{total.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
